import { Navigate, Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";
import { useEffect, type ReactNode } from "react";
import { AppLayout } from "./components/AppLayout.js";
import { ErrorBoundary } from "./components/ErrorBoundary.js";
import { FontScaleToggle } from "./components/FontScaleToggle.js";
import { MarketingLayout } from "./components/marketing/MarketingLayout.js";
import { AuthProvider, useAuth } from "./context/AuthContext.js";
import { FontScaleProvider, useFontScale } from "./context/FontScaleContext.js";
import { AdminPage } from "./pages/AdminPage.js";
import { AdminAiCostsPage } from "./pages/AdminAiCostsPage.js";
import { AdminSignupsPage } from "./pages/AdminSignupsPage.js";
import { AdminWaitlistPage } from "./pages/AdminWaitlistPage.js";
import { AuthCallbackPage } from "./pages/AuthCallbackPage.js";
import { ChecklistPage } from "./pages/ChecklistPage.js";
import { CommunityPage } from "./pages/CommunityPage.js";
import { CommunityTemplatePage } from "./pages/CommunityTemplatePage.js";
import { ContactPage } from "./pages/ContactPage.js";
import { ForgotPage } from "./pages/ForgotPage.js";
import { HowItWorksPage } from "./pages/HowItWorksPage.js";
import { InvitePage } from "./pages/InvitePage.js";
import { LandingPage } from "./pages/LandingPage.js";
import { ListEditorPage } from "./pages/ListEditorPage.js";
import { LoginPage } from "./pages/LoginPage.js";
import { MyChecklistsPage } from "./pages/MyChecklistsPage.js";
import { MyListsPage } from "./pages/MyListsPage.js";
import { MyTemplatesPage } from "./pages/MyTemplatesPage.js";
import { NotFoundPage } from "./pages/NotFoundPage.js";
import { PrivacyPage } from "./pages/PrivacyPage.js";
import { ProfilePage } from "./pages/ProfilePage.js";
import { RegisterPage } from "./pages/RegisterPage.js";
import { TemplateEditorPage } from "./pages/TemplateEditorPage.js";
import {
  CampingChecklistPage,
  OnboardingChecklistPage,
  PackingListPage,
  SharedChecklistPage,
  WeddingChecklistPage,
} from "./pages/UseCasePage.js";
import { VerifyPage } from "./pages/VerifyPage.js";
import { ShareLandingPage } from "./pages/ShareLandingPage.js";
import { RequestAccessPage } from "./pages/RequestAccessPage.js";
import { AccessDecisionPage } from "./pages/AccessDecisionPage.js";
import { getShareSession } from "./lib/shareSession.js";
import { isPrivatePath, MARKETING_PAGES } from "./lib/seo.js";
import { formatPageTitle, routeTitle } from "./lib/pageTitle.js";
import { ToastHost } from "./components/ui/Toast.js";

function PublicOnly({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <BootScreen />;
  if (user) {
    const next = new URLSearchParams(location.search).get("next");
    return <Navigate to={next || "/"} replace />;
  }
  return children;
}

function RequireAuth() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <BootScreen />;
  if (!user) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <Outlet />;
}

function RequireAccess() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  if (loading) return <BootScreen />;
  if (user) return <Outlet />;
  // "new" (Lists/Templates creation) is not a real resource to request access
  // to — an unauthenticated visitor here still needs to sign in, same as before.
  const isNewResource = !params.id || params.id === "new";
  if (isNewResource) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  const segment = location.pathname.split("/")[1];
  const kind = segment === "lists" ? "list" : segment === "templates" ? "template" : "checklist";
  if (getShareSession(kind, params.id)) return <Outlet />;
  return <Navigate to={`/request-access/${kind}/${params.id}`} replace />;
}

// Signed-in visitors keep the full app chrome (nav, account menu); anonymous
// share-link visitors get a bare shell (Task 4.2 - no nav chrome assuming a user).
// The font-size toggle still gets a minimal header here so anonymous viewers
// (who most need larger text on a shared checklist) can reach it too.
function ResourceShell() {
  const { user, loading } = useAuth();
  const { scale: fontScale, cycleScale: cycleFontScale } = useFontScale();
  if (loading) return <BootScreen />;
  if (user) return <AppLayout />;
  return (
    <div className="min-h-full">
      <header className="flex h-12 items-center justify-end border-b border-line bg-white px-4">
        <FontScaleToggle scale={fontScale} onCycle={cycleFontScale} />
      </header>
      <main id="main">
        <Outlet />
      </main>
      <ToastHost />
    </div>
  );
}

function BootScreen() {
  return (
    <div className="flex min-h-full items-center justify-center text-sm text-muted">Loading…</div>
  );
}

function upsertRobots(content: string) {
  let el = document.querySelector('meta[name="robots"]');
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", "robots");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

// Resource pages replace this initial title with the resource name after it loads.
function PageTitle() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const marketing = MARKETING_PAGES.find((p) => p.path === path);
    const segment = routeTitle(path);
    if (marketing) {
      document.title = marketing.title;
    } else {
      document.title = formatPageTitle(segment);
    }

    if (isPrivatePath(path) || (!marketing && segment === "Page not found")) {
      upsertRobots("noindex, nofollow");
    } else if (
      marketing ||
      path === "/community" ||
      /^\/community\/templates\/[^/]+$/.test(path) ||
      /^\/community\/[^/]+$/.test(path)
    ) {
      upsertRobots("index,follow");
    }
  }, [location.pathname]);

  return null;
}

// Public front door (D-31): anonymous visitors see the marketing landing page,
// authenticated users are redirected to /checklists exactly as before.
function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) return <BootScreen />;
  if (user) return <Navigate to="/checklists" replace />;
  return <LandingPage />;
}

// Community is public (D-31 / Task 4.3): anonymous visitors get MarketingLayout;
// signed-in users keep the app shell.
function CommunityShell() {
  const { user, loading } = useAuth();
  if (loading) return <BootScreen />;
  if (!user) {
    return (
      <MarketingLayout>
        <Outlet />
      </MarketingLayout>
    );
  }
  return <AppLayout />;
}

// Contact / Privacy (CUSTOMER_FEEDBACK_PLAN Task 2.2): public for anonymous visitors
// (someone who can't sign in still needs them); signed-in users keep the app shell.
function PublicInfoShell() {
  const { user, loading } = useAuth();
  if (loading) return <BootScreen />;
  if (!user) {
    return (
      <MarketingLayout>
        <Outlet />
      </MarketingLayout>
    );
  }
  return <AppLayout />;
}

export function App() {
  return (
    <FontScaleProvider>
      <AuthProvider>
        <ErrorBoundary>
          <PageTitle />
          <Routes>
            <Route
              path="/login"
              element={
                <PublicOnly>
                  <LoginPage />
                </PublicOnly>
              }
            />
            <Route
              path="/register"
              element={
                <PublicOnly>
                  <RegisterPage />
                </PublicOnly>
              }
            />
            <Route
              path="/verify"
              element={
                <PublicOnly>
                  <VerifyPage />
                </PublicOnly>
              }
            />
            <Route
              path="/forgot"
              element={
                <PublicOnly>
                  <ForgotPage />
                </PublicOnly>
              }
            />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/invite/:token" element={<InvitePage />} />
            <Route path="/s/:token" element={<ShareLandingPage />} />
            <Route path="/request-access/:kind/:id" element={<RequestAccessPage />} />
            <Route path="/share-decision/:token" element={<AccessDecisionPage />} />

            <Route path="/" element={<HomeRoute />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/packing-list" element={<PackingListPage />} />
            <Route path="/shared-checklist" element={<SharedChecklistPage />} />
            <Route path="/wedding-checklist" element={<WeddingChecklistPage />} />
            <Route path="/camping-checklist" element={<CampingChecklistPage />} />
            <Route path="/onboarding-checklist" element={<OnboardingChecklistPage />} />

            <Route element={<PublicInfoShell />}>
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
            </Route>

            <Route element={<CommunityShell />}>
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/community/templates/:id" element={<CommunityTemplatePage />} />
              <Route path="/community/:id" element={<CommunityPage />} />
            </Route>

            <Route element={<AppLayout />}>
              <Route element={<RequireAuth />}>
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/lists" element={<MyListsPage />} />
                <Route path="/templates" element={<MyTemplatesPage />} />
                <Route path="/checklists" element={<MyChecklistsPage />} />
                <Route path="/admin" element={<Navigate to="/admin/settings" replace />} />
                <Route path="/admin/settings" element={<AdminPage />} />
                <Route path="/admin/ai-costs" element={<AdminAiCostsPage />} />
                <Route path="/admin/signups" element={<AdminSignupsPage />} />
                <Route path="/admin/waitlist" element={<AdminWaitlistPage />} />
              </Route>
            </Route>
            <Route element={<ResourceShell />}>
              <Route element={<RequireAccess />}>
                <Route path="/lists/:id" element={<ListEditorPage />} />
                <Route path="/templates/:id" element={<TemplateEditorPage />} />
                <Route path="/checklists/:id" element={<ChecklistPage />} />
              </Route>
            </Route>

            {/* Task 3.2: real 404 — must stay after every concrete route. */}
            <Route element={<PublicInfoShell />}>
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </ErrorBoundary>
      </AuthProvider>
    </FontScaleProvider>
  );
}
